import { createContext, useContext, type ReactNode, type CSSProperties } from "react";
import type { TableDensity } from "../DataTable/DataTable.types";
import { cn } from "@/lib/cn";

const TableDensityContext = createContext<TableDensity>("default");

const densityClasses: Record<TableDensity, { header: string; cell: string; text: string }> = {
  compact:     { header: "bfrs-px-3 bfrs-py-2",   cell: "bfrs-px-3 bfrs-py-2",   text: "bfrs-text-xs" },
  default:     { header: "bfrs-px-4 bfrs-py-2.5", cell: "bfrs-px-4 bfrs-py-3",   text: "bfrs-text-sm" },
  comfortable: { header: "bfrs-px-5 bfrs-py-3",   cell: "bfrs-px-5 bfrs-py-4",   text: "bfrs-text-sm" }
};

export interface TableProps {
  children?: ReactNode;
  density?: TableDensity;
  minWidth?: number | string;
  maxHeight?: number | string;
  className?: string;
  tableClassName?: string;
  "aria-label"?: string;
}

export function Table({
  children,
  density = "default",
  minWidth,
  maxHeight,
  className,
  tableClassName,
  "aria-label": ariaLabel = "Table"
}: TableProps) {
  const scrollStyle: CSSProperties = maxHeight !== undefined ? { maxHeight } : {};
  const tableStyle: CSSProperties = minWidth !== undefined ? { minWidth } : {};

  return (
    <TableDensityContext.Provider value={density}>
      <div className={cn("bfrs-overflow-hidden bfrs-rounded-md bfrs-border bfrs-border-border bfrs-bg-surface-card", className)}>
        <div className="bfrs-overflow-auto" style={scrollStyle}>
          <table
            aria-label={ariaLabel}
            className={cn("bfrs-w-full bfrs-border-separate bfrs-border-spacing-0 bfrs-text-left", tableClassName)}
            style={tableStyle}
          >
            {children}
          </table>
        </div>
      </div>
    </TableDensityContext.Provider>
  );
}

export interface TheadProps {
  children?: ReactNode;
  sticky?: boolean;
  className?: string;
}

export function Thead({ children, sticky = false, className }: TheadProps) {
  return (
    <thead className={cn(sticky && "bfrs-sticky bfrs-top-0 bfrs-z-30", className)}>
      {children}
    </thead>
  );
}

export interface TbodyProps {
  children?: ReactNode;
  className?: string;
}

export function Tbody({ children, className }: TbodyProps) {
  return <tbody className={className}>{children}</tbody>;
}

export interface TfootProps {
  children?: ReactNode;
  className?: string;
}

export function Tfoot({ children, className }: TfootProps) {
  return <tfoot className={className}>{children}</tfoot>;
}

export interface TrProps {
  children?: ReactNode;
  onClick?: () => void;
  className?: string;
}

export function Tr({ children, onClick, className }: TrProps) {
  return (
    <tr
      className={cn(
        "bfrs-group/row bfrs-bg-surface-card bfrs-transition",
        "hover:bfrs-bg-surface-app",
        onClick && "bfrs-cursor-pointer",
        className
      )}
      onClick={onClick}
    >
      {children}
    </tr>
  );
}

export interface ThProps {
  children?: ReactNode;
  align?: "left" | "center" | "right";
  colSpan?: number;
  className?: string;
}

export function Th({ children, align = "left", colSpan, className }: ThProps) {
  const density = useContext(TableDensityContext);
  return (
    <th
      scope="col"
      colSpan={colSpan}
      className={cn(
        "bfrs-h-[46px] bfrs-border-b bfrs-border-border bfrs-bg-surface-app bfrs-align-middle",
        "bfrs-text-[10.5px] bfrs-font-bold bfrs-uppercase bfrs-tracking-[0.07em] bfrs-text-text-secondary",
        densityClasses[density].header,
        align === "center" && "bfrs-text-center",
        align === "right" && "bfrs-text-right",
        className
      )}
    >
      {children}
    </th>
  );
}

export interface TdProps {
  children?: ReactNode;
  align?: "left" | "center" | "right";
  colSpan?: number;
  className?: string;
}

export function Td({ children, align = "left", colSpan, className }: TdProps) {
  const density = useContext(TableDensityContext);
  return (
    <td
      colSpan={colSpan}
      className={cn(
        "bfrs-border-b bfrs-border-border bfrs-bg-surface-card bfrs-align-middle bfrs-text-text-secondary",
        "group-hover/row:bfrs-bg-surface-app",
        densityClasses[density].cell,
        densityClasses[density].text,
        align === "center" && "bfrs-text-center",
        align === "right" && "bfrs-text-right",
        className
      )}
    >
      {children}
    </td>
  );
}
